// /home/szymon/kreator2/frontend/src/components/editor/hooks/useEditorLocalCallbacks.js
// v1.1.2 – Zmieniono logikę skalowania SVG dla lepszej jakości

import { useCallback, useEffect, useMemo } from "react";
// Importujemy funkcję do obliczania centrowania
import { calculateCenteredX } from '../snapUtils';

export function useEditorLocalCallbacks({
  pages,
  setPages,
  currentPage,
  selectedShapeId,
  setSelectedShapeId,
  pushToHistory, // Odbieramy funkcję do zapisywania w lokalnej historii
  changesCountRef,
  publicMode,
  isAccepted,
  doAutoSave,
  handleManualSave,
  backendUrl
}) {
  // Pomocnicza do modyfikowania obecnej strony
  const updateCurrentPage = useCallback(
    (fn) => {
      setPages((prev) => {
        const arr = [...prev];
        if (currentPage < 0 || currentPage >= arr.length) return prev;
        const copyPage = { ...arr[currentPage] };
        arr[currentPage] = fn(copyPage);
        return arr;
      });
    },
    [setPages, currentPage]
  );

  // Znajdujemy aktualnie zaznaczony obiekt
  const foundObj = useMemo(() => {
    if (!pages || pages.length === 0 || currentPage < 0 || currentPage >= pages.length || !selectedShapeId) return null;
    const pg = pages[currentPage];
    let obj = pg.texts.find((t) => t.id === selectedShapeId);
    if (obj) return { ...obj, _type: "text" };
    obj = pg.images.find((im) => im.id === selectedShapeId);
    if (obj) return { ...obj, _type: "image" };
    obj = pg.svgs.find((sv) => sv.id === selectedShapeId);
    if (obj) return { ...obj, _type: "svg" };
    return null;
  }, [pages, currentPage, selectedShapeId]);

  // Odznaczanie zablokowanych
  useEffect(() => {
    if (foundObj && publicMode && foundObj.flags?.locked) {
      setSelectedShapeId(null);
    }
  }, [foundObj, publicMode, setSelectedShapeId]);

  // Auto-zapis i licznik zmian (bez zmian)
  const doAutoSaveLocal = useCallback(() => { handleManualSave(pages); }, [handleManualSave, pages]);
  const incChangesAndMaybeAutoSave = useCallback(() => { changesCountRef.current++; if (changesCountRef.current >= 5) { doAutoSaveLocal(); changesCountRef.current = 0; } }, [changesCountRef, doAutoSaveLocal]);

  // --- Callbacki modyfikujące stan (ZMIENIONO handleChangeTextLocal) ---
  const handleChangeTextLocal = useCallback((updateData) => {
    if (isAccepted && publicMode) return;
    if (!updateData?.id) return; // Potrzebujemy ID

    // Znajdź oryginalny obiekt PRZED aktualizacją stanu
    const originalObj = pages[currentPage]?.texts.find(t => t.id === updateData.id);
    if (!originalObj) {
      console.warn(`[handleChangeTextLocal] Nie znaleziono obiektu o ID: ${updateData.id}`);
      return;
    }

    // Zapisz w historii, jeśli to zaznaczony obiekt
    if (foundObj && foundObj.id === updateData.id && foundObj._type === "text") {
      pushToHistory({ ...originalObj }); // Zapisujemy stan *przed* zmianą
    }

    let finalUpdateData = { ...updateData };

    // --- Dodana logika centrowania ---
    const isCentered = originalObj.alignment === 'center';
    const isPersonalized = originalObj.flags?.personalize || originalObj.flags?.personalization;

    // Sprawdzamy, czy dostaliśmy nową szerokość i czy obiekt wymaga centrowania
    if (finalUpdateData.width !== undefined && isCentered && isPersonalized) {
      const currentPageData = pages[currentPage];
      if (currentPageData) {
        // Potrzebujemy zaimportować calculateCenteredX z ../snapUtils
        // Zakładamy, że import został dodany na górze pliku
        const newX = calculateCenteredX(
          { ...originalObj, width: finalUpdateData.width }, // Użyj nowej szerokości do obliczeń
          currentPageData.guides || [],
          currentPageData.baseWidth || 800,
          currentPageData.baseHeight || 600
        );

        if (newX !== null && Math.abs(newX - (originalObj.x || 0)) > 0.1) {
          console.log(`[handleChangeTextLocal] Centering applied for ${originalObj.id}. New X: ${newX}`);
          finalUpdateData.x = newX; // Dodaj obliczone X do danych aktualizacji
        }
      }
    }
    // --- Koniec logiki centrowania ---

    updateCurrentPage((pg) => {
      pg.texts = pg.texts.map((t) => {
        if (t.id === finalUpdateData.id) {
          const mergedObject = { ...t, ...finalUpdateData };
          // --- ADD LOGGING ---
          console.log(`[handleChangeTextLocal] Updating ID: ${t.id}`);
          console.log(`  Original t:`, JSON.stringify(t));
          console.log(`  finalUpdateData:`, JSON.stringify(finalUpdateData));
          console.log(`  Merged object:`, JSON.stringify(mergedObject));
          // --- END LOGGING ---
          return mergedObject;
        }
        return t;
      });
      return pg;
    });
    incChangesAndMaybeAutoSave();
  }, [isAccepted, publicMode, pages, currentPage, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]); // Dodano 'pages' i 'currentPage' do zależności

  const handleChangeImageLocal = useCallback((chg) => { if (isAccepted && publicMode) return; if (!chg?.id) return; if (foundObj && foundObj.id === chg.id && foundObj._type === "image") { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { pg.images = pg.images.map((im) => im.id === chg.id ? { ...im, ...chg } : im); return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);
  const handleChangeSvgLocal = useCallback((chg) => { if (isAccepted && publicMode) return; if (!chg?.id) return; if (foundObj && foundObj.id === chg.id && foundObj._type === "svg") { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { pg.svgs = pg.svgs.map((sv) => (sv.id === chg.id ? { ...sv, ...chg } : sv)); return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);
  const handleColorChangeLocal = useCallback((obj, colorArr) => { if (!obj || !colorArr) return; if (isAccepted && publicMode) return; if (foundObj && foundObj.id === obj.id) { pushToHistory({ ...foundObj }); } if (obj._type === "text") { handleChangeTextLocal({ id: obj.id, color: colorArr }); } else if (obj._type === "svg") { handleChangeSvgLocal({ id: obj.id, color: colorArr }); } }, [isAccepted, publicMode, foundObj, pushToHistory, handleChangeTextLocal, handleChangeSvgLocal]);
  const handleLayerUpLocal = useCallback((shapeId) => { if (isAccepted && publicMode) return; if (!shapeId) return; if (foundObj && foundObj.id === shapeId) { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { let fnd = false; pg.texts = pg.texts.map(t => t.id === shapeId ? (fnd=true, {...t, subZ: (t.subZ||0)+1}) : t); if(!fnd) pg.images = pg.images.map(im => im.id === shapeId ? (fnd=true, {...im, subZ: (im.subZ||0)+1}) : im); if(!fnd) pg.svgs = pg.svgs.map(sv => sv.id === shapeId ? {...sv, subZ: (sv.subZ||0)+1} : sv); return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);
  const handleLayerDownLocal = useCallback((shapeId) => { if (isAccepted && publicMode) return; if (!shapeId) return; if (foundObj && foundObj.id === shapeId) { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { let fnd=false; pg.texts = pg.texts.map(t => t.id === shapeId ? (fnd=true, {...t, subZ: (t.subZ||0)-1}) : t); if(!fnd) pg.images = pg.images.map(im => im.id === shapeId ? (fnd=true, {...im, subZ: (im.subZ||0)-1}) : im); if(!fnd) pg.svgs = pg.svgs.map(sv => sv.id === shapeId ? {...sv, subZ: (sv.subZ||0)-1} : sv); return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);
  const handleMoveToTopLocal = useCallback(() => { if (!selectedShapeId) return; if (isAccepted && publicMode) return; if (foundObj && foundObj.id === selectedShapeId) { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { const all = [...pg.texts, ...pg.images, ...pg.svgs]; const maxZ = Math.max(...all.map((i) => i.subZ || 0), 0); pg.texts = pg.texts.map((t) => t.id === selectedShapeId ? { ...t, subZ: maxZ + 1 } : t ); pg.images = pg.images.map((m) => m.id === selectedShapeId ? { ...m, subZ: maxZ + 1 } : m ); pg.svgs = pg.svgs.map((s) => s.id === selectedShapeId ? { ...s, subZ: maxZ + 1 } : s ); return pg; }); incChangesAndMaybeAutoSave(); }, [ selectedShapeId, isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave ]);
  const handleMoveToBottomLocal = useCallback(() => { if (!selectedShapeId) return; if (isAccepted && publicMode) return; if (foundObj && foundObj.id === selectedShapeId) { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { const all = [...pg.texts, ...pg.images, ...pg.svgs]; const minZ = Math.min(...all.map((i) => i.subZ || 0), 0); pg.texts = pg.texts.map((t) => t.id === selectedShapeId ? { ...t, subZ: minZ - 1 } : t ); pg.images = pg.images.map((m) => m.id === selectedShapeId ? { ...m, subZ: minZ - 1 } : m ); pg.svgs = pg.svgs.map((s) => s.id === selectedShapeId ? { ...s, subZ: minZ - 1 } : s ); return pg; }); incChangesAndMaybeAutoSave(); }, [ selectedShapeId, isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave ]);
  const handleAddNewTextLocal = useCallback(() => { if (isAccepted && publicMode) return; updateCurrentPage((pg) => { let maxSubZ = 0; [...pg.texts, ...pg.images, ...pg.svgs].forEach((obj) => { if ((obj.subZ || 0) > maxSubZ) { maxSubZ = obj.subZ || 0; } }); const newId = "text-" + Date.now(); pg.texts = [ ...pg.texts, { id: newId, text: "Nowy tekst", x: 50, y: 50, draggable: true, color: [0, 0, 0], alignment: "left", fontName: "sans-serif", fontSize: 24, rotation: 0, scaleX: 1, scaleY: 1, zGroup: 3, subZ: maxSubZ + 1, flags: { noPrint: false, gold: false, locked: false, personalize: false } } ]; return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, updateCurrentPage, incChangesAndMaybeAutoSave]);

  // Zmodyfikowano handleImageDroppedLocal, aby renderowała SVG do offscreen canvas
  // i używała alternatywnego podejścia do skalowania
  const handleImageDroppedLocal = useCallback(async (args) => {
    console.log("[handleImageDroppedLocal] Entered function with args:", args);
    const { file, name, base64, customSvgUrl, customPos, customSize, isRebus } = args;

    if (isAccepted && publicMode) {
        console.log("[handleImageDroppedLocal] Exiting: Project accepted in public mode.");
        return;
    }

    const finalFile = isRebus ? file : file;
    const finalName = isRebus ? file?.name : name;
    const initialX = isRebus ? customPos?.x : 50;
    const initialY = isRebus ? customPos?.y : 50;
    const initialW = isRebus ? customSize?.w : 200; // Oryginalne/odczytane wymiary
    const initialH = isRebus ? customSize?.h : 200;

    if (!finalFile || typeof finalFile !== "object" || !finalFile.size) {
        console.log("[handleImageDroppedLocal] Invalid file detected:", finalFile);
        alert("Nie wykryto poprawnego pliku...");
        return;
    }

    console.log(`[handleImageDroppedLocal] Uploading file (isRebus: ${!!isRebus}):`, finalName);
    try {
        const baseUrl = backendUrl.replace(/\/+$/, "");
        const url = publicMode ? `${baseUrl}/client/uploads` : `${baseUrl}/admin/uploads`;
        const formData = new FormData();
        formData.append("file", finalFile);
        const uploadRes = await fetch(url, { method: "POST", credentials: "include", body: formData });
        if (!uploadRes.ok) {
            throw new Error(`Błąd uploadu (status=${uploadRes.status})`);
        }
        const uploadData = await uploadRes.json();
        if (!uploadData.success || !uploadData.filePath) {
            throw new Error("Serwer nie zwrócił ścieżki do pliku.");
        }
        const filePath = uploadData.filePath;
        const rawUrlPrefix = publicMode ? `${baseUrl}/client/raw-file?path=` : `${baseUrl}/admin/raw-file?path=`;
        const rawUrl = rawUrlPrefix + encodeURIComponent(filePath);
        console.log(`[handleImageDroppedLocal] Upload successful. File path: ${filePath}, Raw URL: ${rawUrl}`);

        const isSvg = (finalName || "").toLowerCase().endsWith(".svg");
        let konvaWidth = initialW;     // Wymiary obiektu Konva
        let konvaHeight = initialH;
        let konvaScaleX = 1;           // Skala obiektu Konva
        let konvaScaleY = 1;
        let konvaImageSource = rawUrl; // Domyślnie URL

        if (isSvg) {
            // Dla SVG, używamy oryginalnych wymiarów przekazanych z drag&drop
            // Nie potrzebujemy już ładować obrazka, aby je poznać
            konvaWidth = initialW;
            konvaHeight = initialH;
            konvaScaleX = 1;
            konvaScaleY = 1;
            konvaImageSource = rawUrl; // Zapisujemy URL jako źródło
            console.log(`[handleImageDroppedLocal] Processing SVG. Using dimensions: ${konvaWidth}x${konvaHeight}, Source URL: ${rawUrl}`);
        } else {
            // Obsługa obrazów rastrowych (logika bez zmian - próbujemy załadować, aby uzyskać wymiary)
            try {
                const img = new window.Image();
                img.crossOrigin = "anonymous";
                await new Promise((resolve, reject) => {
                    img.onload = () => resolve(true);
                    img.onerror = reject;
                    img.src = rawUrl;
                });
                const origW = img.naturalWidth || 100;
                const origH = img.naturalHeight || 100;
                // Obliczamy skalę, aby zmieścić obraz na stronie, ale nie większy niż 1:1
                const currentPageData = pages[currentPage] || { baseWidth: 800, baseHeight: 600 }; // Domyślne wymiary strony
                const scale = Math.min((currentPageData.baseWidth || 800) / origW, (currentPageData.baseHeight || 600) / origH, 1);
                konvaWidth = Math.round(origW * scale); // Ustawiamy wymiary Konva
                konvaHeight = Math.round(origH * scale);
                console.log(`[handleImageDroppedLocal] Raster image loaded. Original: ${origW}x${origH}, Scaled: ${konvaWidth}x${konvaHeight}`);
            } catch (imgLoadError) {
                console.warn("[handleImageDroppedLocal] Could not load raster image to determine size, using default:", imgLoadError);
                konvaWidth = initialW; // Używamy domyślnych/przekazanych wymiarów
                konvaHeight = initialH;
            }
            konvaImageSource = rawUrl; // Źródłem jest URL
            konvaScaleX = 1; // Skala jest już uwzględniona w konvaWidth/konvaHeight
            konvaScaleY = 1;
        }

        // Dodajemy obiekt do stanu strony
        updateCurrentPage((pg) => {
            let maxSubZ = 0;
            [...pg.texts, ...pg.images, ...pg.svgs].forEach((obj) => {
                if ((obj.subZ || 0) > maxSubZ) {
                    maxSubZ = obj.subZ || 0;
                }
            });
            const newId = (isSvg ? "svg-" : "img-") + Date.now();
            const commonProps = {
                id: newId,
                x: initialX,
                y: initialY,
                draggable: true,
                width: konvaWidth,      // Używamy obliczonych/oryginalnych wymiarów
                height: konvaHeight,     // Używamy obliczonych/oryginalnych wymiarów
                rotation: 0,
                scaleX: konvaScaleX,    // Zwykle 1, chyba że skalowanie jest potrzebne inaczej
                scaleY: konvaScaleY,    // Zwykle 1
                filePath,               // Zapisujemy ścieżkę do pliku
                src: konvaImageSource,  // Zapisujemy URL jako źródło
                subZ: maxSubZ + 1,
                flags: { noPrint: false, gold: false, locked: false, personalize: false }
            };

            if (isSvg) {
                const svgObj = {
                    ...commonProps,
                    // Usunięto 'image' - używamy 'src' z commonProps
                    originalWidth: konvaWidth, // Zapisujemy wymiary jako oryginalne
                    originalHeight: konvaHeight,
                    zGroup: 2,
                    color: [0, 0, 0] // Domyślny kolor dla SVG
                };
                console.log("[handleImageDroppedLocal] Adding SVG object to state (using URL source):", svgObj);
                pg.svgs = [...pg.svgs, svgObj];
            } else {
                const imgObj = {
                    ...commonProps,
                    // 'src' jest już w commonProps
                    zGroup: 1
                };
                console.log("[handleImageDroppedLocal] Adding Image object to state (using URL source):", imgObj);
                pg.images = [...pg.images, imgObj];
            }
            return pg;
        });
        console.log("[handleImageDroppedLocal] State update requested with new object.");
        incChangesAndMaybeAutoSave();
    } catch (ex) {
        console.error("[handleImageDroppedLocal] Upload Error:", ex);
        alert("Nie udało się wgrać pliku: " + ex.message);
    }
}, [isAccepted, publicMode, updateCurrentPage, incChangesAndMaybeAutoSave, backendUrl]);


  const handleToggleFlagLocal = useCallback((flagName) => { if (!selectedShapeId) return; if (isAccepted && publicMode) return; if (foundObj && foundObj.id === selectedShapeId) { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { function toggleIn(arr) { return arr.map((obj) => { if (obj.id === selectedShapeId) { const currentFlags = obj.flags || { noPrint: false, gold: false, locked: false, personalize: false }; return { ...obj, flags: { ...currentFlags, [flagName]: !currentFlags[flagName] } }; } return obj; }); } pg.texts = toggleIn(pg.texts); pg.images = toggleIn(pg.images); pg.svgs = toggleIn(pg.svgs); return pg; }); incChangesAndMaybeAutoSave(); }, [ selectedShapeId, isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave ]);

  // Usunięcie błędnych funkcji pomocniczych i definicji lokalnego undo/redo
  // function localUndoObj() { ... }
  // function localRedoObj() { ... }
  // const handleLocalUndo = useCallback(() => { ... });
  // const handleLocalRedo = useCallback(() => { ... });

  // Obsługa Delete (bez zmian)
  useEffect(() => { const handleKeyDown = (e) => { if (e.key === "Delete") { if (!selectedShapeId) return; if (isAccepted && publicMode) { return; } if (foundObj) { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => ({ ...pg, texts: pg.texts.filter((t) => t.id !== selectedShapeId), images: pg.images.filter((im) => im.id !== selectedShapeId), svgs: pg.svgs.filter((sv) => sv.id !== selectedShapeId) })); setSelectedShapeId(null); } }; window.addEventListener("keydown", handleKeyDown); return () => { window.removeEventListener("keydown", handleKeyDown); }; }, [ selectedShapeId, isAccepted, publicMode, setSelectedShapeId, updateCurrentPage, foundObj, pushToHistory ]);

  // ZMIANA: Usunięto handleLocalUndo i handleLocalRedo z obiektu zwracanego
  return {
    foundObj,
    handleAddNewTextLocal,
    handleImageDroppedLocal,
    handleChangeTextLocal,
    handleChangeImageLocal,
    handleChangeSvgLocal,
    handleColorChangeLocal,
    handleLayerUpLocal,
    handleLayerDownLocal,
    handleMoveToTopLocal,
    handleMoveToBottomLocal,
    handleToggleFlagLocal
    // handleLocalUndo, // Usunięto
    // handleLocalRedo  // Usunięto
  };
}
